import {Trans} from "@lingui/react";
import {forwardRef, type ComponentProps, type ReactNode} from "react";
import Spinner from "../../shared/ui/Spinner";
import {Box, Col, Row, css} from "../../ui/Box";
import type {InputProps, SelectProps, TextAreaProps} from "../../ui/Input";
import {Input, Select, TextArea} from "../../ui/Input";
import type {NeoField, UseMetaArgs} from "./lib/create-neo-form";

const shouldShowError = (meta: UseMetaArgs) => {
  if (meta.errors === null || meta.errors.length === 0) return false;
  return (meta.hasChanged && !meta.isFocussed) || meta.submitAttemptCount > 0;
};

type WithLabelProps = {
  field: NeoField<any>;
  label?: ReactNode | null;
  hint?: ReactNode;
  children?: ReactNode;
};
export const WithLabel = ({field, label = field.name, children, hint}: WithLabelProps) => {
  const {useMeta, id} = field;
  const {errors, isPending} = useMeta((meta) => ({
    errors: shouldShowError(meta) ? meta.errors! : [],
    isPending: meta.isPending,
  }));
  return (
    <Col sp="4">
      {label && (
        <label htmlFor={id} className={css({color: "secondary", weight: "bold", size: "14_tight"})}>
          {label}
          {isPending && <Spinner size="16" inline />}
        </label>
      )}
      {children}
      {errors.length > 0 && (
        <Row wrap sp="4">
          {errors.map((e, idx) =>
            "code" in e ? (
              <Box style={{color: "red"}} key={e.code}>
                <Trans
                  id={`validation.${e.code}`}
                  message={e.message}
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  values={{requirement: e.ctx.requirement, received: e.ctx.received}}
                />
              </Box>
            ) : (
              <div key={idx}>other error...</div>
            )
          )}
        </Row>
      )}
      {hint && typeof hint === "string" ? <div style={{color: "gray"}}>{hint}</div> : hint}
    </Col>
  );
};

type MinusFieldProps<T> = Omit<T, "onChange" | "onBlur" | "onFocus" | "id" | "value" | "ref">;

type TextFieldProps = {
  field: NeoField;
  label?: ReactNode | null;
  hint?: ReactNode;
} & MinusFieldProps<InputProps>;
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  ({field, label, hint, ...rest}, ref) => {
    const {useValue, useMeta, getHandlers, name, id} = field;
    const showErrors = useMeta((meta) => shouldShowError(meta));
    return (
      <WithLabel field={field} label={label} hint={hint}>
        <Input
          name={name}
          id={id}
          {...rest}
          {...getHandlers({...rest, ref})}
          value={useValue()}
          hasError={showErrors}
        />
      </WithLabel>
    );
  }
);

type TextAreaFieldProps = {
  field: NeoField;
  label?: ReactNode | null;
  hint?: ReactNode;
} & MinusFieldProps<TextAreaProps>;
export const TextAreaField = forwardRef<HTMLInputElement, TextAreaFieldProps>(
  ({field, label, hint, ...rest}, ref) => {
    const {useValue, useMeta, getHandlers, name, id} = field;
    const showErrors = useMeta((meta) => shouldShowError(meta));
    return (
      <WithLabel field={field} label={label} hint={hint}>
        <TextArea
          name={name}
          id={id}
          {...rest}
          {...getHandlers({...rest, ref})}
          value={useValue()}
          hasError={showErrors}
        />
      </WithLabel>
    );
  }
);

type SelectFieldProps = {
  field: NeoField<string | null>;
  label?: ReactNode | null;
  hint?: ReactNode;
} & MinusFieldProps<SelectProps>;
export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(
  ({field, label, hint, ...rest}, ref) => {
    const {getHandlers, useValue, useMeta, id, name} = field;
    const showErrors = useMeta((meta) => shouldShowError(meta));
    return (
      <WithLabel field={field} label={label} hint={hint}>
        <Select
          name={name}
          id={id}
          {...rest}
          {...getHandlers({...rest, ref})}
          value={useValue()}
          hasError={showErrors}
        />
      </WithLabel>
    );
  }
);

type CheckboxFieldProps = Omit<ComponentProps<"input">, "onChange"> & {
  field: NeoField<boolean>;
  label?: ReactNode | null;
  hint?: ReactNode;
};

export const CheckboxField = forwardRef<HTMLInputElement, CheckboxFieldProps>(
  ({field, label, hint, ...rest}, ref) => {
    const {useValue, id, name, getHandlers} = field;
    const {onChange, ...restHandlers} = getHandlers({...rest, ref});
    return (
      <Row sp="8">
        <input
          type="checkbox"
          name={name}
          id={id}
          {...rest}
          {...restHandlers}
          onChange={(e) => onChange(e.target.checked)}
          checked={useValue()}
        />
        <WithLabel field={field} label={label} hint={hint} />
      </Row>
    );
  }
);
